/**
 * 系统更新公告 Markdown 渲染组件（基础语法）
 */

import type { ReactElement, ReactNode } from 'react'
import { cn } from '@/lib/utils'

type InlineTokenKind = 'link' | 'code' | 'bold' | 'italic'

interface InlineTokenMatch {
  kind: InlineTokenKind
  index: number
  match: RegExpExecArray
}

interface SystemUpdateMarkdownProps {
  content: string
  className?: string
}

/**
 * 判断是否为安全链接
 * @param url 链接地址
 * @returns 是否安全
 */
function isSafeLink(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

/**
 * 在文本中查找最靠前的行内语法 token。
 * @param text 文本内容
 * @returns token 信息或 null
 */
function findEarliestInlineToken(text: string): InlineTokenMatch | null {
  const patterns: readonly { kind: InlineTokenKind; regex: RegExp }[] = [
    { kind: 'link', regex: /\[([^\]]+)\]\(([^)]+)\)/ },
    { kind: 'code', regex: /`([^`]+)`/ },
    { kind: 'bold', regex: /\*\*([^*]+)\*\*/ },
    { kind: 'italic', regex: /\*([^*]+)\*/ },
  ] as const

  let best: InlineTokenMatch | null = null
  for (const pattern of patterns) {
    const match = pattern.regex.exec(text)
    if (match === null || match.index < 0) {
      continue
    }

    if (best === null || match.index < best.index) {
      best = {
        kind: pattern.kind,
        index: match.index,
        match,
      }
      continue
    }

    if (match.index === best.index) {
      const currentOrder = patterns.findIndex((item) => item.kind === pattern.kind)
      const bestOrder = patterns.findIndex((item) => item.kind === best?.kind)
      if (currentOrder >= 0 && bestOrder >= 0 && currentOrder < bestOrder) {
        best = {
          kind: pattern.kind,
          index: match.index,
          match,
        }
      }
    }
  }

  return best
}

/**
 * 解析行内 Markdown 语法
 * @param text 文本
 * @param keyPrefix key 前缀
 * @returns ReactNode 数组
 */
function parseInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let rest = text
  let index = 0

  while (rest.length > 0) {
    const token = findEarliestInlineToken(rest)
    if (token === null) {
      nodes.push(rest)
      break
    }

    if (token.index > 0) {
      nodes.push(rest.slice(0, token.index))
    }

    const fullText = token.match[0]
    const tokenKey = `${keyPrefix}-${String(index)}`

    if (token.kind === 'link') {
      const label = token.match[1] ?? ''
      const url = token.match[2] ?? ''
      if (isSafeLink(url)) {
        nodes.push(
          <a
            key={tokenKey}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-4"
          >
            {label}
          </a>
        )
      } else {
        nodes.push(label)
      }
    }

    if (token.kind === 'code') {
      const codeText = token.match[1] ?? ''
      nodes.push(
        <code
          key={tokenKey}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.92em]"
        >
          {codeText}
        </code>
      )
    }

    if (token.kind === 'bold') {
      const boldText = token.match[1] ?? ''
      nodes.push(
        <strong key={tokenKey} className="font-semibold text-foreground">
          {boldText}
        </strong>
      )
    }

    if (token.kind === 'italic') {
      const italicText = token.match[1] ?? ''
      nodes.push(
        <em key={tokenKey} className="italic">
          {italicText}
        </em>
      )
    }

    rest = rest.slice(token.index + fullText.length)
    index += 1
  }

  return nodes
}

/**
 * 判断行是否为区块起始行
 * @param line 文本行
 * @returns 是否为区块起始
 */
function isBlockStart(line: string): boolean {
  const trimmed = line.trim()
  if (trimmed === '') {
    return true
  }
  return (
    /^#{1,6}\s+/.test(trimmed) ||
    trimmed.startsWith("```") ||
    /^[-*]\s+/.test(trimmed) ||
    /^\d+\.\s+/.test(trimmed) ||
    /^>\s?/.test(trimmed)
  )
}

/**
 * Markdown 渲染组件
 * @param props 组件属性
 * @returns ReactElement
 */
export function SystemUpdateMarkdown(props: SystemUpdateMarkdownProps): ReactElement {
  const { content, className } = props
  const normalized = content.replace(/\r\n/g, '\n')
  const lines = normalized.split('\n')
  const elements: ReactElement[] = []

  let lineIndex = 0
  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? ''
    const trimmed = line.trim()

    if (trimmed === '') {
      lineIndex += 1
      continue
    }

    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim()
      const codeLines: string[] = []
      lineIndex += 1
      while (lineIndex < lines.length) {
        const currentLine = lines[lineIndex] ?? ''
        if (currentLine.trim().startsWith('```')) {
          lineIndex += 1
          break
        }
        codeLines.push(currentLine)
        lineIndex += 1
      }

      elements.push(
        <div key={`code-${String(lineIndex)}`} className="overflow-x-auto rounded-lg border bg-muted/40">
          {language !== '' ? (
            <div className="border-b px-3 py-1.5 text-xs text-muted-foreground">
              {language}
            </div>
          ) : null}
          <pre className="px-3 py-3 text-sm leading-6">
            <code>{codeLines.join('\n')}</code>
          </pre>
        </div>
      )
      continue
    }

    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed)
    if (headingMatch !== null) {
      const level = headingMatch[1]?.length ?? 1
      const text = headingMatch[2] ?? ''
      const contentNodes = parseInlineMarkdown(text, `heading-${String(lineIndex)}`)
      const key = `heading-${String(lineIndex)}`

      if (level === 1) {
        elements.push(
          <h1 key={key} className="text-2xl font-semibold leading-9 text-foreground">
            {contentNodes}
          </h1>
        )
      } else if (level === 2) {
        elements.push(
          <h2 key={key} className="text-xl font-semibold leading-8 text-foreground">
            {contentNodes}
          </h2>
        )
      } else if (level === 3) {
        elements.push(
          <h3 key={key} className="text-lg font-semibold leading-7 text-foreground">
            {contentNodes}
          </h3>
        )
      } else {
        elements.push(
          <h4 key={key} className="text-base font-semibold leading-7 text-foreground">
            {contentNodes}
          </h4>
        )
      }

      lineIndex += 1
      continue
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = []
      while (lineIndex < lines.length) {
        const currentLine = (lines[lineIndex] ?? '').trim()
        const itemMatch = /^[-*]\s+(.*)$/.exec(currentLine)
        if (itemMatch === null) {
          break
        }
        items.push(itemMatch[1] ?? '')
        lineIndex += 1
      }

      elements.push(
        <ul key={`ul-${String(lineIndex)}`} className="list-disc space-y-1 pl-6 text-foreground">
          {items.map((item, idx) => (
            <li key={`li-${String(lineIndex)}-${String(idx)}`}>
              {parseInlineMarkdown(item, `ul-${String(lineIndex)}-${String(idx)}`)}
            </li>
          ))}
        </ul>
      )
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = []
      while (lineIndex < lines.length) {
        const currentLine = (lines[lineIndex] ?? '').trim()
        const itemMatch = /^\d+\.\s+(.*)$/.exec(currentLine)
        if (itemMatch === null) {
          break
        }
        items.push(itemMatch[1] ?? '')
        lineIndex += 1
      }

      elements.push(
        <ol key={`ol-${String(lineIndex)}`} className="list-decimal space-y-1 pl-6 text-foreground">
          {items.map((item, idx) => (
            <li key={`oli-${String(lineIndex)}-${String(idx)}`}>
              {parseInlineMarkdown(item, `ol-${String(lineIndex)}-${String(idx)}`)}
            </li>
          ))}
        </ol>
      )
      continue
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = []
      while (lineIndex < lines.length) {
        const currentLine = (lines[lineIndex] ?? '').trim()
        const quoteMatch = /^>\s?(.*)$/.exec(currentLine)
        if (quoteMatch === null) {
          break
        }
        quoteLines.push(quoteMatch[1] ?? '')
        lineIndex += 1
      }

      elements.push(
        <blockquote
          key={`quote-${String(lineIndex)}`}
          className="border-l-4 border-primary/40 bg-muted/40 px-4 py-2 text-muted-foreground"
        >
          {quoteLines.map((quote, idx) => (
            <p key={`quote-line-${String(lineIndex)}-${String(idx)}`}>
              {parseInlineMarkdown(quote, `quote-${String(lineIndex)}-${String(idx)}`)}
            </p>
          ))}
        </blockquote>
      )
      continue
    }

    const paragraphLines: string[] = []
    while (lineIndex < lines.length) {
      const currentLine = lines[lineIndex] ?? ''
      if (isBlockStart(currentLine) && currentLine.trim() !== '') {
        break
      }
      if (currentLine.trim() === '') {
        break
      }
      paragraphLines.push(currentLine.trim())
      lineIndex += 1
    }

    const paragraphText = paragraphLines.join(' ')
    elements.push(
      <p key={`p-${String(lineIndex)}`} className="leading-7 text-foreground">
        {parseInlineMarkdown(paragraphText, `p-${String(lineIndex)}`)}
      </p>
    )

    if (lineIndex < lines.length && (lines[lineIndex] ?? '').trim() === '') {
      lineIndex += 1
    }
  }

  return <div className={cn('space-y-4', className)}>{elements}</div>
}
